import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppState, type UploadOutcome } from "../../state/AppStateContext";
import { useUi } from "../../state/ui";

/**
 * Modal for adding user ontologies: drag-and-drop or file picker, Turtle
 * only for now. Files are parsed immediately — malformed ones are rejected
 * with the parser's error message and never enter the store.
 */
export default function UploadDialog() {
  const { uploadOpen, closeUpload } = useUi();
  const { uploadOntologies } = useAppState();
  const navigate = useNavigate();
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [outcomes, setOutcomes] = useState<UploadOutcome[] | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (uploadOpen) {
      setOutcomes(null);
      setIsDragging(false);
      setIsProcessing(false);
    }
  }, [uploadOpen]);

  useEffect(() => {
    if (!uploadOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeUpload();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [uploadOpen, closeUpload]);

  if (!uploadOpen) return null;

  const handleFiles = async (fileList: FileList | File[]) => {
    const files = [...fileList].filter((f) => /\.(ttl|turtle)$/i.test(f.name));
    const skipped = [...fileList].filter((f) => !/\.(ttl|turtle)$/i.test(f.name));
    if (files.length === 0 && skipped.length === 0) return;

    setIsProcessing(true);
    const results = files.length > 0 ? await uploadOntologies(files) : [];
    setIsProcessing(false);
    setOutcomes([
      ...results,
      ...skipped.map((f) => ({
        fileName: f.name,
        ok: false,
        error: "Unsupported file type — only Turtle (.ttl) files are supported for now.",
      })),
    ]);
  };

  const succeeded = outcomes?.filter((o) => o.ok) ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 pt-[14vh] backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closeUpload();
      }}
    >
      <div
        className="oe-pop-in w-full max-w-lg overflow-hidden rounded-xl border border-[var(--oe-border)] bg-[var(--oe-surface)]"
        style={{ boxShadow: "var(--oe-shadow-lg)" }}
        role="dialog"
        aria-modal="true"
        aria-label="Upload ontology"
      >
        <div className="flex items-center justify-between border-b border-[var(--oe-border)] px-5 py-3.5">
          <h2 className="text-sm font-semibold text-[var(--oe-text)]">Upload ontology</h2>
          <button
            type="button"
            onClick={closeUpload}
            className="rounded-md p-1 text-[var(--oe-text-muted)] hover:bg-[var(--oe-hover)] hover:text-[var(--oe-text)]"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5">
          <div
            className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
              isDragging
                ? "border-[var(--oe-accent)] bg-[var(--oe-accent-soft)]"
                : "border-[var(--oe-border-strong)]"
            }`}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragging(false);
              void handleFiles(event.dataTransfer.files);
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--oe-text-muted)]" aria-hidden="true">
              <path d="M12 16V4m0 0 4 4m-4-4L8 8" />
              <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
            </svg>
            <p className="text-sm text-[var(--oe-text)]">
              Drop Turtle files here, or{" "}
              <button
                type="button"
                className="font-medium text-[var(--oe-accent)] hover:underline"
                onClick={() => inputRef.current?.click()}
              >
                browse
              </button>
            </p>
            <p className="text-xs text-[var(--oe-text-muted)]">
              .ttl · parsed and validated locally, stored in your browser
            </p>
            <input
              ref={inputRef}
              type="file"
              accept=".ttl,.turtle,text/turtle"
              multiple
              className="hidden"
              onChange={(event) => {
                if (event.target.files) void handleFiles(event.target.files);
                event.target.value = "";
              }}
            />
          </div>

          {isProcessing && (
            <div className="mt-4">
              <div className="oe-progress h-1 rounded-full bg-[var(--oe-surface-raised)]" />
              <p className="mt-2 text-xs text-[var(--oe-text-muted)]">Parsing…</p>
            </div>
          )}

          {outcomes && outcomes.length > 0 && (
            <ul className="mt-4 space-y-2">
              {outcomes.map((outcome) => (
                <li
                  key={outcome.fileName + (outcome.datasetId ?? "")}
                  className={`rounded-lg border px-3 py-2 text-sm ${
                    outcome.ok
                      ? "border-[var(--oe-border)] bg-[var(--oe-surface-raised)]/60"
                      : "border-[var(--oe-danger)]/40 bg-[var(--oe-danger)]/5"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {outcome.ok ? (
                      <span className="text-green-600 dark:text-green-400" aria-hidden="true">✓</span>
                    ) : (
                      <span className="text-[var(--oe-danger)]" aria-hidden="true">✕</span>
                    )}
                    <span className="min-w-0 flex-1 truncate font-mono text-xs text-[var(--oe-text)]">
                      {outcome.fileName}
                    </span>
                    {outcome.ok && outcome.tripleCount !== undefined && (
                      <span className="shrink-0 text-xs text-[var(--oe-text-muted)]">
                        {outcome.tripleCount.toLocaleString()} triples
                      </span>
                    )}
                  </div>
                  {!outcome.ok && outcome.error && (
                    <p className="mt-1 pl-6 text-xs leading-relaxed text-[var(--oe-danger)]">
                      {outcome.error}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}

          {succeeded.length > 0 && (
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeUpload}
                className="rounded-lg px-3 py-1.5 text-sm text-[var(--oe-text-muted)] hover:bg-[var(--oe-hover)]"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  closeUpload();
                  navigate(
                    `/explore/ontologies?dataset=${encodeURIComponent(succeeded[0]!.datasetId!)}`,
                  );
                }}
                className="rounded-lg bg-[var(--oe-accent)] px-3.5 py-1.5 text-sm font-medium text-[var(--oe-accent-contrast)] hover:opacity-90"
              >
                View {succeeded.length === 1 ? succeeded[0]!.label : `${succeeded.length} ontologies`} →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
